import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatTime12 } from '../lib/dateUtils'
import { STATUS_COLORS } from '../lib/constants'
import { googleMapsUrl } from '../lib/mapUtils'
import { displayCrewName } from '../lib/crew'

export default function JobDetailsModal({ isOpen, onClose, job, onEdit, onDelete, onCancelJob, onEditProperty, onJobUpdated, onViewHistory }) {
  const [showRemoveOptions, setShowRemoveOptions] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    setShowRemoveOptions(false)
  }, [job?.id])

  if (!isOpen || !job) return null

  const prop = job.property || {}
  const colors = STATUS_COLORS[job.status] || STATUS_COLORS.scheduled

  const handleDeleteFromHistory = () => {
    if (window.confirm('Permanently delete this job from all history?')) {
      onDelete(job.id)
      onClose()
    }
  }

  const handleCancelAndRemove = () => {
    onCancelJob(job.id)
    onClose()
  }

  const Section = ({ title, children }) => (
    <div className="py-3 border-b border-gray-100 last:border-b-0">
      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{title}</h4>
      {children}
    </div>
  )

  const InfoRow = ({ label, value, isLink }) => {
    if (!value) return null
    return (
      <div className="flex justify-between py-1">
        <span className="text-sm text-gray-500">{label}</span>
        {isLink ? (
          <a href={`tel:${value}`} className="text-sm text-brand-600 font-medium">{value}</a>
        ) : (
          <span className="text-sm text-gray-900 font-medium text-right max-w-[60%]">{value}</span>
        )}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`px-5 py-4 ${colors.bg} border-b ${colors.border}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h2 className={`text-lg font-bold ${colors.text} break-words`}>{prop.name || 'Job Details'}</h2>
              <div className="flex items-start gap-1.5 mt-0.5">
                <p className={`text-sm opacity-75 ${colors.text} break-words min-w-0 flex-1`}>{prop.address}</p>
                {prop.address && (
                  <a
                    href={googleMapsUrl(prop.address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open in Google Maps"
                    className={`p-0.5 rounded hover:bg-black/10 ${colors.text} opacity-75 hover:opacity-100 flex-shrink-0`}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </a>
                )}
                {prop.id && (
                  <button
                    onClick={() => navigate(`/property-history?propertyId=${prop.id}`)}
                    title="View property history"
                    className={`p-0.5 rounded hover:bg-black/10 ${colors.text} opacity-75 hover:opacity-100 flex-shrink-0`}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <button onClick={onClose} className={`p-1 rounded-lg hover:bg-black/10 ${colors.text}`}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}>
              {job.status}
            </span>

            {prop.do_not_service && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-400">
                ⛔ DO NOT SERVICE
              </span>
            )}
            {prop.past_due_invoice && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700 border border-orange-400">
                ⚠️ PAST DUE INVOICE
              </span>
            )}
            {job.flag === 'urgent' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-300">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" />
                </svg>
                Urgent
              </span>
            )}
            <span className="text-sm text-gray-600">
              {formatTime12(job.start_time)} – {formatTime12(job.end_time)}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-2">
          {/* Standing Directions */}
          {prop.standing_directions && (
            <Section title="Standing Directions">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">{prop.standing_directions}</p>
              </div>
            </Section>
          )}

          {/* Job Comments */}
          {job.comments && (
            <Section title="Job Comments">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">{job.comments}</p>
              </div>
            </Section>
          )}

          {/* Property Notes (internal) */}
          {prop.property_notes && (
            <Section title="Property Notes (Internal)">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-700">{prop.property_notes}</p>
              </div>
            </Section>
          )}

          {/* Property Details */}
          <Section title="Property Details">
            <InfoRow label="Phone" value={prop.phone} isLink />
            <InfoRow label="Billing Email" value={prop.billing_email} />
            <InfoRow label="Management Company" value={prop.management_company} />
            <InfoRow label="Unit Count" value={prop.unit_count} />
          </Section>

          {/* Property Manager */}
          {(prop.property_manager_name || prop.property_manager_phone || prop.property_manager_email) && (
            <Section title="Property Manager">
              <InfoRow label="Name" value={prop.property_manager_name} />
              <InfoRow label="Phone" value={prop.property_manager_phone} isLink />
              <InfoRow label="Email" value={prop.property_manager_email} />
            </Section>
          )}

          {/* Maintenance Supervisor */}
          {(prop.maintenance_supervisor_name || prop.maintenance_supervisor_phone) && (
            <Section title="Maintenance Supervisor">
              <InfoRow label="Name" value={prop.maintenance_supervisor_name} />
              <InfoRow label="Phone" value={prop.maintenance_supervisor_phone} isLink />
            </Section>
          )}

          {/* Job Info */}
          <Section title="Job Info">
            <InfoRow label="Date" value={job.job_date} />
            <InfoRow label="Time" value={`${formatTime12(job.start_time)} – ${formatTime12(job.end_time)}`} />
            <InfoRow label="Crew" value={displayCrewName(job.crew_name)} />
            <InfoRow label="Status" value={job.status} />
          </Section>
        </div>

        {/* Footer */}
        {showRemoveOptions ? (
          <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">What would you like to do?</p>
            <button
              onClick={handleCancelAndRemove}
              className="w-full px-4 py-2.5 text-left rounded-lg border border-orange-200 bg-orange-50 hover:bg-orange-100 transition-colors"
            >
              <div className="text-sm font-medium text-orange-800">Cancel &amp; Remove from Calendar</div>
              <div className="text-xs text-orange-600 mt-0.5">Keeps the job in history as cancelled</div>
            </button>
            <button
              onClick={handleDeleteFromHistory}
              className="w-full px-4 py-2.5 text-left rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 transition-colors"
            >
              <div className="text-sm font-medium text-red-800">Delete from History</div>
              <div className="text-xs text-red-600 mt-0.5">Permanently removes from all records</div>
            </button>
            <button
              onClick={() => setShowRemoveOptions(false)}
              className="w-full px-4 py-2 text-sm text-gray-500 hover:text-gray-700"
            >
              Never mind
            </button>
          </div>
        ) : (
          <div className="px-5 py-4 border-t border-gray-100 flex justify-between gap-3 bg-gray-50">
            <button
              onClick={() => setShowRemoveOptions(true)}
              className="px-4 py-2 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 border border-red-200"
            >
              Remove Job
            </button>
            <div className="flex gap-2">
              {onViewHistory && (
                <button
                  onClick={() => onViewHistory(job.property)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                >
                  History
                </button>
              )}
              {onEditProperty && (
                <button
                  onClick={() => onEditProperty(job.property)}
                  className="px-4 py-2 border border-brand-300 text-brand-700 text-sm font-medium rounded-lg hover:bg-brand-50"
                >
                  Edit Property
                </button>
              )}
              <button
                onClick={() => {
                  onClose()
                  onEdit(job)
                }}
                className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700"
              >
                Edit Job
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
